import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Loader2, AtSign, Check, X } from 'lucide-react';
import { usePromptQueue, PromptPriority } from '@/contexts/PromptQueueContext';
import { useQueryClient } from '@tanstack/react-query';
import { invalidateUserProfile } from '@/lib/queryKeys';

export function UsernamePromptDialog() {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const { requestPrompt, releasePrompt, isActivePrompt } = usePromptQueue();
  const [username, setUsername] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [hasCheckedProfile, setHasCheckedProfile] = useState(
    () => localStorage.getItem('username-prompt-checked') === 'true'
  );

  // Check if user needs to set username (only if they already have a name)
  useEffect(() => {
    const checkUsername = async () => {
      if (authLoading || !user || hasCheckedProfile) return;
      if (localStorage.getItem('username-prompt-dismissed') === 'true') return;
      // Skip on auth page
      if (location.pathname === '/auth') return;
      // Skip while the onboarding flow is still active — it now collects the
      // username itself. Querying mid-onboarding (before the user hits Continue)
      // would see a null username and queue a redundant prompt that then fires
      // on the next page even though the handle was just saved.
      if (location.pathname === '/' && localStorage.getItem('grounded_onboarding_complete') !== 'true') return;

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('first_name, last_name, username')
        .eq('id', user.id)
        .single();
      
      if (error) {
        console.error('Error checking profile:', error);
        return;
      }
      
      setHasCheckedProfile(true);
      localStorage.setItem('username-prompt-checked', 'true');
      // Only show username prompt if user has name but no username
      // NamePromptDialog handles the case where name is missing
      // The last name is optional, so a first name alone counts as a name.
      const hasName = !!profile?.first_name;
      if (hasName && !profile?.username) {
        requestPrompt('username', PromptPriority.USERNAME);
      }
    };

    const settleTimer = setTimeout(checkUsername, 1500);
    return () => clearTimeout(settleTimer);
  }, [user, authLoading, hasCheckedProfile, location.pathname]);

  // Check username availability with debounce
  useEffect(() => {
    if (!username || username.length < 3) {
      setIsAvailable(null);
      return;
    }

    // Validate format
    const usernameRegex = /^[a-zA-Z0-9._]+$/;
    if (!usernameRegex.test(username)) {
      setIsAvailable(false);
      return;
    }

    const timer = setTimeout(async () => {
      setIsChecking(true);
      
      const { data, error } = await supabase
        .from('profiles')
        .select('id')
        .eq('username', username.toLowerCase())
        .maybeSingle();
      
      if (error) {
        console.error('Error checking username:', error);
        setIsAvailable(null);
      } else {
        setIsAvailable(data === null);
      }
      
      setIsChecking(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [username]);

  const handleSave = async () => {
    if (!user || !username || !isAvailable) return;
    
    setIsSaving(true);
    
    const { error } = await supabase
      .from('profiles')
      .update({ 
        username: username.toLowerCase(),
        updated_at: new Date().toISOString()
      })
      .eq('id', user.id);
    
    if (error) {
      if (error.code === '23505') {
        toast({
          title: 'Username taken',
          description: 'This username was just claimed. Please try another.',
          variant: 'destructive',
        });
        setIsAvailable(false);
      } else {
        toast({
          title: 'Error saving username',
          description: error.message,
          variant: 'destructive',
        });
      }
    } else {
      toast({
        title: 'Username set!',
        description: `You can now be found as @${username.toLowerCase()}`,
      });
      invalidateUserProfile(queryClient, user.id);
      releasePrompt('username');
    }
    
    setIsSaving(false);
  };

  const handleSkip = () => {
    releasePrompt('username');
    localStorage.setItem('username-prompt-dismissed', 'true');
    toast({
      title: 'Skipped for now',
      description: 'You can set your username anytime in your profile settings.',
    });
  };

  const isValidFormat = username.length >= 3 && /^[a-zA-Z0-9._]+$/.test(username);

  return (
    <Dialog open={isActivePrompt('username')} onOpenChange={(o) => { if (!o) handleSkip(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AtSign className="h-5 w-5 text-primary" />
            Choose your username
          </DialogTitle>
          <DialogDescription>
            Pick a unique username so others can find and recognize you in the community.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="username">Username</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">@</span>
              <Input
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value.replace(/\s/g, ''))}
                placeholder="your_username"
                className="pl-8 pr-10"
                maxLength={30}
                autoComplete="off"
              />
              {username.length >= 3 && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {isChecking ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  ) : isAvailable === true ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : isAvailable === false ? (
                    <X className="h-4 w-4 text-destructive" />
                  ) : null}
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              3-30 characters. Letters, numbers, dots, and underscores only.
            </p>
            {username.length >= 3 && !isChecking && (
              <p className={`text-xs ${isAvailable ? 'text-green-600' : 'text-destructive'}`}>
                {isAvailable 
                  ? '✓ Username is available' 
                  : !isValidFormat 
                    ? 'Invalid characters. Use only letters, numbers, dots, and underscores.'
                    : 'Username is already taken'}
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="ghost" onClick={handleSkip}>
            Skip for now
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={!isValidFormat || !isAvailable || isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Set Username'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
